<template>
  <div class="card">
    <div class="card-header bg-primary text-white">
      <h5 class="mb-0">线路信息</h5>
    </div>
    <div class="card-body">
      <!-- 顶部两行压缩布局：
           第1行：线路名称 + 方向选择（twoWay时显示）
           第2行：线路类型（独占一行） -->

      <div class="row g-2 align-items-end mb-2">
        <div class="col-8">
          <label class="form-label mb-1">线路名称</label>
          <input type="text" class="form-control form-control-sm" :value="lineName" placeholder="请输入线路名称" @input="updateLineName">
        </div>
        <div class="col-4" v-if="lineType === 'twoWay'">
          <label class="form-label mb-1">方向</label>
          <div class="btn-group d-flex" role="group">
            <button type="button" class="btn btn-sm btn-outline-primary" :class="{ 'active': currentDirection === 'up' }" @click="switchDirection('up')">上行</button>
            <button type="button" class="btn btn-sm btn-outline-primary" :class="{ 'active': currentDirection === 'down' }" @click="switchDirection('down')">下行</button>
          </div>
        </div>
      </div>

      <!-- 多语言设置（默认折叠：摘要 + 展开） - 移至线路名称下方 -->
      <div class="mb-2 p-2 border rounded bg-light">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center gap-2">
            <div class="form-check form-switch m-0">
              <input class="form-check-input" type="checkbox" id="enableMultiLanguage" v-model="localLanguageSettings.enabled" @change="onLanguageEnabledChange">
              <label class="form-check-label" for="enableMultiLanguage">多语言</label>
            </div>
            <small class="text-muted" v-if="localLanguageSettings.enabled">语言数：{{ localLanguageSettings.count }}</small>
            <small class="text-muted" v-else>已关闭</small>
          </div>
          <button type="button" class="btn btn-sm btn-outline-secondary icon-btn" @click="showLangAdvanced = !showLangAdvanced" :disabled="!localLanguageSettings.enabled" :aria-label="showLangAdvanced ? '收起设置' : '展开设置'">
            <svg v-if="!showLangAdvanced" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-down">
              <path d="m6 9 6 6 6-6"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-up">
              <path d="m18 15-6-6-6 6"/>
            </svg>
          </button>
        </div>

        <div v-if="localLanguageSettings.enabled && showLangAdvanced" class="mt-2">
          <div class="mb-2">
            <label class="form-label">支持的语言数量</label>
            <select class="form-select form-select-sm" v-model="localLanguageSettings.count" @change="onLanguageSettingsChange">
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
          </div>
          <div class="mb-2">
            <label class="form-label">语言设置</label>
            <div class="row mb-1">
              <div class="col">
                <div class="input-group input-group-sm">
                  <span class="input-group-text">语言 1</span>
                  <input type="text" class="form-control form-control-sm" :value="getLanguageName(0)" placeholder="例如：中文" @input="updateLanguageName(0, $event)">
                  <input type="text" class="form-control form-control-sm" :value="getLanguageCode(0)" placeholder="例如：zh" @input="updateLanguageCode(0, $event)">
                </div>
              </div>
            </div>
            <div class="row mb-1" v-for="index in activeLanguageIndices" :key="`lang-${index}-${localLanguageSettings.count}`">
              <div class="col">
                <div class="input-group input-group-sm">
                  <span class="input-group-text">语言 {{ index + 1 }}</span>
                  <input 
                    type="text" 
                    class="form-control form-control-sm" 
                    :value="getLanguageName(index)" 
                    placeholder="例如：英文/English" 
                    @input="updateLanguageName(index, $event)"
                    :key="`name-${index}-${localLanguageSettings.count}`"
                  >
                  <input 
                    type="text" 
                    class="form-control form-control-sm" 
                    :value="getLanguageCode(index)" 
                    placeholder="例如：en" 
                    @input="updateLanguageCode(index, $event)"
                    :key="`code-${index}-${localLanguageSettings.count}`"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row g-2 mb-2">
        <div class="col-12">
          <div class="section-header mb-1">
            <label class="form-label mb-0">线路类型</label>
          </div>
          <div class="btn-group w-100 align-buttons" role="group">
            <input type="radio" class="btn-check" name="lineType" id="twoWayLine" :value="'twoWay'" :checked="lineType === 'twoWay'" @change="updateLineType">
            <label class="btn btn-sm btn-outline-secondary" for="twoWayLine">双向线路</label>
            <input type="radio" class="btn-check" name="lineType" id="oneWayLine" :value="'oneWay'" :checked="lineType === 'oneWay'" @change="updateLineType">
            <label class="btn btn-sm btn-outline-secondary" for="oneWayLine">单向/环线</label>
          </div>
        </div>
      </div>

      <!-- 文件导入区域（压缩版） -->
      <div class="mb-2">
        <div>
          <label class="form-label mb-1">上传站名</label>
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <button type="button" class="btn btn-sm btn-primary" @click="showTextImportModal" title="批量粘贴站名，每行一个，按顺序排列">
              输入站名
            </button>
            <input type="file" accept=".txt" @change="handleFileImport" ref="fileInput" style="display: none;">
            <button type="button" class="btn btn-sm btn-outline-primary" @click="$refs.fileInput.click()" title="从本地选择站名txt文件">
              选择文件
            </button>
            <button class="btn btn-sm btn-outline-danger" @click="clearStations" v-if="currentStations.length > 0">清空站点</button>
            <span v-if="selectedFileName" class="text-muted small" :title="selectedFileName">
              {{ selectedFileName }}
            </span>
            <button class="btn btn-sm btn-outline-success ms-auto" @click="generateReverseStations" 
                    v-if="lineType === 'twoWay' && currentDirection === 'down' && upDirectionStations.length > 0 && downDirectionStations.length === 0">
              自动生成下行
            </button>
          </div>
        </div>
      </div>

      <!-- 站点列表 -->
      <div class="station-list" id="stationList">
        <div v-if="!currentStations.length" class="text-center text-muted py-5">
          请导入或创建站名列表
        </div>
        <div v-else 
             @drop="onDrop" 
             @dragover="onDragOver"
             @dragenter="onDragEnter"
             @dragleave="onDragLeave">
          <div v-for="(station, index) in currentStations" :key="station.id" 
               class="station-item" 
               :class="{ 
                 'active': selectedStationIndex === index,
                 'drag-over': draggedIndex !== null && draggedIndex !== index && dropIndicatorIndex === index
               }"
               draggable="true"
               @dragstart="onDragStart($event, index)"
               @click="selectStation(index)">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <span class="drag-handle me-2">≡</span>
                <span class="badge bg-secondary me-2">{{ index + 1 }}</span>
                {{ getStationDisplayName(station) }}
                <span v-if="index === 0" class="badge bg-success badge-station-type">起点站</span>
                <span v-else-if="index === currentStations.length - 1" class="badge bg-danger badge-station-type">终点站</span>
              </div>
              <div class="station-actions">
                <button class="btn btn-sm btn-outline-primary rounded-circle icon-circle-btn me-1" @click.stop="editStation(index)" title="编辑" aria-label="编辑">
                  <i class="bi bi-pencil"></i>
                </button>
                <button class="btn btn-sm btn-outline-danger rounded-circle icon-circle-btn" @click.stop="removeStation(index)" title="删除" aria-label="删除">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trash-2">
                    <path d="M3 6h18"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    <line x1="10" y1="11" x2="10" y2="17"/>
                    <line x1="14" y1="11" x2="14" y2="17"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <!-- 拖拽辅助线 -->
          <div 
            v-if="dropIndicatorIndex !== null && dropIndicatorIndex === currentStations.length" 
            class="drop-indicator">
          </div>
        </div>
      </div>
      
      <!-- 站点排序已改为拖拽排序 -->
    </div>
  </div>

  <!-- 文本批量导入模态框 -->
  <div class="modal fade" id="textImportModal" tabindex="-1" aria-labelledby="textImportModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="textImportModalLabel">输入站名</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label for="textImportContent" class="form-label">请输入站名列表（每行一个站名）：</label>
            <textarea 
              class="form-control" 
              id="textImportContent" 
              rows="10" 
              v-model="textImportContent"
              placeholder="请输入站名，每行一个，例如：&#10;北京站&#10;北京西站&#10;北京南站"
            ></textarea>
          </div>
          <div class="mb-3">
            <small class="text-muted">
              <i class="bi bi-info-circle me-1"></i>
              请确保每行只有一个站名，空行将被忽略。第一个站名为起点站，最后一个为终点站。
            </small>
          </div>

          <!-- 多语言批量写入现有站名（仅当多语言开启且数量>1时显示） -->
          <div v-if="localLanguageSettings && localLanguageSettings.enabled && (parseInt(localLanguageSettings.count) || 1) > 1" class="p-2 border rounded bg-light">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <strong>批量写入到其他语言</strong>
              <small class="text-muted">不会新增或删除站点，仅更新现有站点的多语言名称</small>
            </div>
            <div class="row g-2 align-items-end mb-2">
              <div class="col-6">
                <label class="form-label mb-1">覆盖策略</label>
                <select class="form-select form-select-sm" v-model="overwriteMode">
                  <option value="overwrite">覆盖所有</option>
                  <option value="fill-blank">仅填空</option>
                </select>
              </div>
              <div class="col-6">
                <label class="form-label mb-1">起始偏移（从该序号的后一位开始写入）</label>
                <input type="number" min="0" class="form-control form-control-sm" v-model.number="startIndex">
              </div>
            </div>
            <div class="d-flex justify-content-end flex-wrap gap-2">
              <template v-for="(lang, idx) in visibleLanguages" :key="'ml-btn-' + idx">
                <button 
                  v-if="lang && lang.code && lang.code !== 'zh'"
                  type="button" 
                  class="btn btn-sm btn-outline-primary"
                  :disabled="!hasValidText"
                  @click="applyTextToLanguage(lang.code)"
                >
                  写入 {{ lang.name || lang.code }} ({{ lang.code }})
                </button>
              </template>
              <span v-if="!visibleLanguages || visibleLanguages.length === 0" class="text-muted small">暂无可用语言</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">取消</button>
          <button type="button" class="btn btn-primary" @click="processTextImport">确认导入</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StationListPanel',
  emits: [
    'updateLineName',
    'lineTypeChanged', 
    'directionChanged',
    'selectStation',
    'editStation', 
    'importStations',
    'removeStation',
    'clearStations',
    'updateLanguageSettings',
    'moveStation',
    // 新增：批量写入现有站点的多语言名称
    'bulkUpdateStationNames'
  ],
  props: {
    upDirectionStations: {
      type: Array,
      required: true
    },
    downDirectionStations: {
      type: Array,
      required: true
    },
    currentDirection: {
      type: String,
      required: true
    },
    selectedStationIndex: {
      type: Number,
      default: -1
    },
    languageSettings: {
      type: Object,
      required: true
    },
    lineName: {
      type: String,
      default: ''
    },
    lineType: {
      type: String,
      default: 'twoWay'
    }
  },
  data() {
    return {
      nextId: 1,
      selectedFileName: '', // 选中的文件名
      localLanguageSettings: JSON.parse(JSON.stringify(this.languageSettings)), // 深拷贝语言设置
      savedLanguageConfig: null, // 保存用户的完整语言配置
      saveTimeout: null, // 保存定时器
      textImportContent: '', // 文本批量导入的内容
      textImportModal: null, // 模态框实例
      // 拖拽相关数据
      draggedIndex: null,
      dropIndicatorIndex: null,
      showLangAdvanced: false,
      // 多语言写入控制
      overwriteMode: 'overwrite', // overwrite | fill-blank | skip-blank-lines
      startIndex: 0
    };
  },
  computed: {
    currentStations() {
      return this.currentDirection === 'up' ? this.upDirectionStations : this.downDirectionStations;
    },
        activeLanguageIndices() {
      // 确保语言数量为数字类型，避免 undefined 错误
      const count = parseInt(this.localLanguageSettings.count) || 1;
      // 只要count > 1就显示额外的语言输入框（从第2个语言开始）
      if (count <= 1) {
        return [];
      }
      const result = Array.from({ length: count - 1 }, (_, i) => i + 1);
      return result;
    },
    visibleLanguages() {
      // 确保语言数量为数字类型，避免 undefined 错误
      const count = parseInt(this.localLanguageSettings.count) || 1;
      return this.localLanguageSettings.languages.slice(0, count);
    },
    // 是否有有效的文本行（用于启用“写入语言”按钮）
    hasValidText() {
      if (!this.textImportContent) return false;
      const lines = this.textImportContent
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
      return lines.length > 0;
    }
  },
  methods: {
    // 更新线路名称
    updateLineName(event) {
      this.$emit('updateLineName', event.target.value);
    },
    
    // 更新线路类型
    updateLineType(event) {
      this.$emit('lineTypeChanged', event.target.value);
    },
    
    switchDirection(direction) {
      if (this.currentDirection !== direction) {
        this.$emit('directionChanged', direction);
      }
    },
    
    selectStation(index) {
      this.$emit('selectStation', index);
    },
    
    editStation(index) {
      this.$emit('editStation', index);
    },
    
    // 自动生成下行站点（上行站点的反向顺序）
    generateReverseStations() {
      if (this.upDirectionStations.length === 0) {
        alert('请先导入上行站点数据');
        return;
      }
      
      // 获取当前最大ID，为下行站点分配新的ID范围
      let nextId = this.getNextId();
      
      // 创建上行站点的深拷贝，然后反转顺序
      const reversedStations = JSON.parse(JSON.stringify(this.upDirectionStations))
        .reverse()
        .map(station => {
          // 为每个站点分配唯一的ID
          station.id = nextId++;
          return station;
        });
      
      // 导入反转后的站点作为下行数据
      this.$emit('importStations', reversedStations, 'down');
      
      // 发送生成成功的消息给父组件，由父组件统一显示提示
      this.$emit('reverseSuccess', reversedStations.length);
    },
    
    // 处理文件导入
    handleFileImport(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // 更新选中的文件名
      this.selectedFileName = file.name;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          const stationNames = content.split('\n').filter(name => name.trim() !== '');
          
          if (stationNames.length === 0) {
            alert('文件内容为空或格式不正确');
            return;
          }
          
          const stations = this.createStationsFromNames(stationNames);
          this.$emit('importStations', stations, this.currentDirection);
          
          // 清空input，允许再次选择同一文件
          event.target.value = '';
        } catch (error) {
          console.error('处理站名文件失败:', error);
          alert('处理站名文件失败: ' + error.message);
        }
      };
      
      reader.onerror = () => {
        alert('读取文件失败');
      };
      
      reader.readAsText(file);
    },
    
    // 从站名数组创建站点对象数组
    createStationsFromNames(stationNames) {
      let nextId = Math.max(
        ...this.upDirectionStations.map(s => s.id || 0),
        ...this.downDirectionStations.map(s => s.id || 0),
        0
      ) + 1;
      
      return stationNames.map(name => ({
        id: nextId++,
        name: name.trim(),
        customArrival: false,
        arrivalTemplate: '',
        customDeparture: false,
        departureTemplate: '',
        audioSequence: [],
        arrivalAudioSequence: [],
        departureAudioSequence: [],
        latitude: null,
        longitude: null,
        arrivalDistance: 50,
        departureDistance: 50,
        arrivalLEDContent: '',
        departureLEDContent: ''
      }));
    },
    
    // 获取下一个可用的ID
    getNextId() {
      let maxId = Math.max(
        ...this.upDirectionStations.map(s => s.id || 0),
        ...this.downDirectionStations.map(s => s.id || 0),
        0
      );
      return maxId + 1;
    },
    
    // 移除站点
    removeStation(index) {
      if (confirm(`确定要删除站点 "${this.currentStations[index].name}" 吗？`)) {
        this.$emit('removeStation', index, this.currentDirection);
      }
    },
    
    // 清空所有站点
    clearStations() {
      if (confirm(`确定要清空${this.currentDirection === 'up' ? '上行' : '下行'}的所有站点吗？`)) {
        this.$emit('clearStations', this.currentDirection);
      }
    },
    
    // 站点移动功能已移至拖拽排序
    

    
    // 语言设置变化时自动保存
    ensureLanguageArraySize(count) {
      // 确保languages数组存在
      if (!Array.isArray(this.localLanguageSettings.languages)) {
        this.localLanguageSettings.languages = [];
      }
      
      // 确保至少有默认的中文语言
      if (this.localLanguageSettings.languages.length === 0) {
        this.localLanguageSettings.languages.push({ name: '中文', code: 'zh' });
      }
      
      // 添加新的语言槽位
      while (this.localLanguageSettings.languages.length < count) {
        this.localLanguageSettings.languages.push({ name: '', code: '' });
      }
      
      // 移除多余的语言槽位
      while (this.localLanguageSettings.languages.length > count) {
        this.localLanguageSettings.languages.pop();
      }
    },

    getLanguageName(index) {
      return (this.localLanguageSettings.languages[index] && this.localLanguageSettings.languages[index].name) || '';
    },
    
    getLanguageCode(index) {
      return (this.localLanguageSettings.languages[index] && this.localLanguageSettings.languages[index].code) || '';
    },
    
    updateLanguageName(index, event) {
      const value = event.target.value;
      // 确保数组和元素存在
      this.ensureLanguageArraySize(Math.max(index + 1, this.localLanguageSettings.count));
      
      if (this.localLanguageSettings.languages[index]) {
        this.localLanguageSettings.languages[index].name = value;
        this.onLanguageSettingsChange();
      }
    },
    
    updateLanguageCode(index, event) {
      const value = event.target.value;
      // 确保数组和元素存在
      this.ensureLanguageArraySize(Math.max(index + 1, this.localLanguageSettings.count));
      
      if (this.localLanguageSettings.languages[index]) {
        this.localLanguageSettings.languages[index].code = value;
        this.onLanguageSettingsChange();
      }
    },

    onLanguageSettingsChange() {
      // 使用防抖延迟保存，避免频繁触发
      clearTimeout(this.saveTimeout);
      this.saveTimeout = setTimeout(() => {
        const updatedSettings = JSON.parse(JSON.stringify(this.localLanguageSettings));
        this.$emit('updateLanguageSettings', updatedSettings);
      }, 500);
    },
    
    onLanguageEnabledChange() {
      if (!this.localLanguageSettings.enabled) {
        // 禁用多语言时保存当前配置
        if (this.localLanguageSettings.count > 1) {
          this.savedLanguageConfig = {
            count: this.localLanguageSettings.count,
            languages: [...this.localLanguageSettings.languages]
          };
        }
        // 设置为1种语言
        this.localLanguageSettings.count = 1;
      } else {
        // 启用多语言时恢复配置
        if (this.savedLanguageConfig && this.savedLanguageConfig.count > 1) {
          this.localLanguageSettings.count = this.savedLanguageConfig.count;
          this.localLanguageSettings.languages = [...this.savedLanguageConfig.languages];
        } else {
          // 默认设置为2种语言
          this.localLanguageSettings.count = 2;
        }
        // 确保数组大小正确
        this.ensureLanguageArraySize(this.localLanguageSettings.count);
      }
      this.onLanguageSettingsChange();
    },

    getStationDisplayName(station) {
      // 优先显示主name字段
      if (station.name) return station.name;
      // 多语言回退，优先显示第一个有值的多语言字段
      if (this.localLanguageSettings && this.localLanguageSettings.languages) {
        for (let i = 0; i < this.localLanguageSettings.languages.length; i++) {
          const code = this.localLanguageSettings.languages[i].code;
          if (code && station['name_' + code]) {
            return station['name_' + code];
          }
        }
      }
      return '未命名';
    },

    exportStations() {
      const exportData = this.currentStations.map(station => {
        const result = { id: station.id };
        if (this.languageSettings.enabled) {
          // 导出所有name_xx
          if (station.name) result.name = station.name;
          if (this.languageSettings.languages) {
            this.languageSettings.languages.forEach(lang => {
              if (lang.code && station['name_' + lang.code]) {
                result['name_' + lang.code] = station['name_' + lang.code];
              }
            });
          }
        } else {
          // 只导出主name
          if (station.name) result.name = station.name;
        }
        // 可根据需要添加其他字段
        return result;
      });
      // 导出exportData为json
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'stations.json';
      a.click();
      URL.revokeObjectURL(url);
    },

    // 显示文本批量导入模态框
    showTextImportModal() {
      this.textImportContent = '';
      if (!this.textImportModal) {
        this.textImportModal = new bootstrap.Modal(document.getElementById('textImportModal'));
      }
      this.textImportModal.show();
    },

    // 处理文本批量导入
    processTextImport() {
      if (!this.textImportContent.trim()) {
        alert('请输入站名内容');
        return;
      }

      // 按行分割文本，并过滤空行
      const lines = this.textImportContent.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      if (lines.length === 0) {
        alert('没有有效的站名内容');
        return;
      }

      // 生成站点数据（ID 从全局最大ID继续递增，避免与现有列表冲突）
      let baseId = this.getNextId();
      const stations = lines.map((stationName, index) => {
        const station = {
          id: baseId++,
          name: stationName,
          audioSequence: [],
          arrivalAudioSequence: [],
          departureAudioSequence: [],
          multiLanguageSequence: [],
          aliasSequence: [],
          latitude: 0,
          longitude: 0,
          arrivalDistance: 50,
          departureDistance: 50
        };

        // 如果启用了多语言，为每种语言创建对应的字段
        if (this.localLanguageSettings.enabled && this.localLanguageSettings.languages) {
          this.localLanguageSettings.languages.forEach(lang => {
            if (lang.code && lang.code !== 'zh') {
              station['name_' + lang.code] = '';
              station['arrivalLEDContent_' + lang.code] = '';
              station['departureLEDContent_' + lang.code] = '';
            }
          });
        }

        return station;
      });

      // 导入站点数据
      this.$emit('importStations', stations, this.currentDirection);

      // 关闭模态框
      this.textImportModal.hide();
      this.textImportContent = '';
      
      // 发送导入成功的消息给父组件，由父组件统一显示提示
      this.$emit('importSuccess', stations.length);
    },

    // 多语言：将文本框的站名批量写入到指定语言的现有站点名称字段
    applyTextToLanguage(langCode) {
      if (!this.textImportContent || !this.textImportContent.trim()) {
        alert('请输入站名内容');
        return;
      }
      if (!langCode) {
        alert('目标语言代码为空');
        return;
      }

      // 解析文本行
      let lines = this.textImportContent.split('\n').map(l => l.trim());
      // 默认过滤空行（除非用户希望保留空行并跳过写入）
      if (this.overwriteMode !== 'skip-blank-lines') {
        lines = lines.filter(l => l.length > 0);
      }

      if (lines.length === 0) {
        alert('没有有效的站名内容');
        return;
      }

      const startIndex = Number(this.startIndex) || 0;
      if (startIndex < 0) {
        alert('起始偏移不能为负数');
        return;
      }

      // 发出事件交由父组件执行写入并做一次性状态更新
      this.$emit('bulkUpdateStationNames', {
        direction: this.currentDirection,
        langCode,
        values: lines,
        overwriteMode: this.overwriteMode,
        startIndex
      });
    },

    // 拖拽相关方法
    onDragStart(event, index) {
      this.draggedIndex = index;
      event.dataTransfer.effectAllowed = 'move';
    },

    onDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      this.updateDropIndicator(event);
    },

    onDragEnter(event) {
      event.preventDefault();
      this.updateDropIndicator(event);
    },

    onDragLeave(event) {
      // 只有当真正离开容器时才清除指示器
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.dropIndicatorIndex = null;
      }
    },

    updateDropIndicator(event) {
      const dropIndex = this.getDropIndex(event);
      this.dropIndicatorIndex = dropIndex;
    },

    onDrop(event) {
      event.preventDefault();
      const dropIndex = this.getDropIndex(event);
      if (this.draggedIndex !== null && dropIndex !== null && this.draggedIndex !== dropIndex) {
        this.moveStation(this.draggedIndex, dropIndex);
      }
      this.draggedIndex = null;
      this.dropIndicatorIndex = null;
    },

    getDropIndex(event) {
      const items = event.currentTarget.querySelectorAll('.station-item');
      const rect = event.currentTarget.getBoundingClientRect();
      const y = event.clientY - rect.top;
      
      for (let i = 0; i < items.length; i++) {
        const itemRect = items[i].getBoundingClientRect();
        const itemY = itemRect.top - rect.top + itemRect.height / 2;
        if (y < itemY) {
          return i;
        }
      }
      return items.length;
    },

    moveStation(fromIndex, toIndex) {
      // 通知父组件移动站点
      this.$emit('moveStation', fromIndex, toIndex, this.currentDirection);
    }
  },
  watch: {
    // 监听多语言设置变化，更新本地数据
    languageSettings: {
      deep: true,
      handler(newVal) {
        this.localLanguageSettings = JSON.parse(JSON.stringify(newVal));
        // 如果当前启用了多语言且语言数量大于1，保存这个配置
        if (newVal.enabled && newVal.count > 1) {
          this.savedLanguageConfig = {
            count: newVal.count,
            languages: JSON.parse(JSON.stringify(newVal.languages))
          };
        }
      }
    },
    // 监听线路类型变化，如果切换到单向线路模式，自动切换到上行方向
    lineType: {
      handler(newVal) {
        if (newVal === 'oneWay') {
          this.switchDirection('up');
        }
        // 通知父组件线路类型变化
        this.$emit('lineTypeChanged', newVal);
      },
      immediate: false
    },
        'localLanguageSettings.count': {
      handler(newCount, oldCount) {
        this.ensureLanguageArraySize(parseInt(newCount) || 1);
        
        // 只在用户手动更改时触发保存（不是初始化时）
        if (oldCount !== undefined && oldCount !== newCount) {
          this.onLanguageSettingsChange();
        }
      },
      immediate: true
    }
  }
};
</script>

<style scoped>
.section-header{display:flex;align-items:center;justify-content:space-between;min-height:32px}
.align-buttons .btn{min-width:96px}
.icon-btn svg{width:18px;height:18px;display:block}
.station-list {
  height: 400px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 10px;
  background-color: white;
}

.station-item {
  padding: 8px 15px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.station-item:hover {
  background-color: #f1f8ff;
}

.station-item.active {
  background-color: #e7f3ff;
  border-left: 3px solid #007bff;
}

.badge-station-type {
  margin-left: 5px;
}

.station-actions {
  display: flex;
  gap: 5px;
}

.icon-circle-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.icon-circle-btn svg {
  width: 16px;
  height: 16px;
  display: block;
}

/* Ensure Bootstrap Icons inside circular buttons have the same size as inline SVGs */
.icon-circle-btn i.bi {
  font-size: 16px;
  line-height: 1;
  display: inline-block;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.custom-file-input {
  display: flex;
  align-items: center;
  gap: 10px;
}

.custom-file-input .btn {
  white-space: nowrap;
}

.custom-file-input span {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 拖拽相关样式 */
.drag-handle {
  font-weight: bold;
  color: #6c757d;
  cursor: grab;
  user-select: none;
}

.drag-handle:active {
  cursor: grabbing;
}

.station-item {
  cursor: move;
  transition: all 0.2s ease;
}

.station-item.drag-over {
  border-top: 2px solid #007bff;
  margin-top: 2px;
}

.drop-indicator {
  height: 2px;
  background-color: #007bff;
  margin: 4px 0;
  border-radius: 1px;
}
</style> 